import React, { useState, useEffect } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import styled from 'styled-components';
import axios from 'axios';

interface FeeSchedule {
  id: number;
  name: string;
  type: 'AUM' | 'FLAT' | 'PERFORMANCE' | 'TIERED';
  rate: number;
  minFee?: number;
  maxFee?: number;
  billingFrequency: 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
  tiers?: { minAum: number; maxAum: number; rate: number }[];
  accountCount: number;
  totalAum: number;
}

interface BillingPeriod {
  id: number;
  billingPeriodId?: string; // UUID for API calls
  periodStart: string;
  periodEnd: string;
  status: 'PENDING' | 'CALCULATED' | 'INVOICED' | 'PAID';
  totalFees: number;
  accountsBilled: number;
  createdAt: string;
}

interface FeeDetail {
  householdName: string;
  accountNumber: string;
  aum: number;
  feeSchedule: string;
  fee: number;
  billingDate: string;
  status: string;
}

const Container = styled.div`
  padding: 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  min-height: calc(100vh - 64px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => {
    if (props.variant === 'primary') {
      return `
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        color: white;
        border: none;
        &:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
        }
      `;
    }
    if (props.variant === 'success') {
      return `
        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
        color: white;
        border: none;
        &:hover {
          background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
        }
      `;
    }
    return `
      background: rgba(255, 255, 255, 0.1);
      color: #e2e8f0;
      border: 1px solid rgba(255, 255, 255, 0.2);
      &:hover {
        background: rgba(255, 255, 255, 0.15);
      }
    `;
  }}
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 1rem;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px 8px 0 0;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.2)' : 'transparent'};
  color: ${props => props.active ? '#3b82f6' : '#94a3b8'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};

  &:hover {
    color: #ffffff;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  color: #e2e8f0;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.status) {
      case 'PAID':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      case 'INVOICED':
        return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
      case 'CALCULATED':
        return 'background: rgba(168, 85, 247, 0.2); color: #a855f7;';
      case 'PENDING':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const TypeBadge = styled.span<{ type: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;

  ${props => {
    switch (props.type) {
      case 'AUM':
        return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
      case 'TIERED':
        return 'background: rgba(168, 85, 247, 0.2); color: #a855f7;';
      case 'FLAT':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      case 'PERFORMANCE':
        return 'background: rgba(239, 68, 68, 0.2); color: #ef4444;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const ScheduleCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(59, 130, 246, 0.3);
  }
`;

const ScheduleName = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
`;

const ScheduleDetail = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #94a3b8;
  margin-top: 0.5rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #ffffff;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #e2e8f0;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #64748b;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  option {
    background: #1e293b;
    color: #ffffff;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const FeeBillingScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'schedules' | 'periods'>('overview');
  const [schedules, setSchedules] = useState<FeeSchedule[]>([]);
  const [periods, setPeriods] = useState<BillingPeriod[]>([]);
  const [currentPeriodFees, setCurrentPeriodFees] = useState<FeeDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewScheduleModal, setShowNewScheduleModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    scheduleName: '',
    feeType: 'AUM_PERCENTAGE',
    baseFeeBps: '',
    flatFeeAmount: '',
    billingFrequency: 'QUARTERLY',
    minimumFee: '',
    description: '',
  });

  // Mock data
  const mockSchedules: FeeSchedule[] = [
    {
      id: 1,
      name: 'Standard AUM',
      type: 'AUM',
      rate: 1.0,
      minFee: 500,
      billingFrequency: 'QUARTERLY',
      accountCount: 85,
      totalAum: 42500000,
    },
    {
      id: 2,
      name: 'High Net Worth Tiered',
      type: 'TIERED',
      rate: 0.75,
      billingFrequency: 'QUARTERLY',
      tiers: [
        { minAum: 0, maxAum: 1000000, rate: 1.0 },
        { minAum: 1000000, maxAum: 5000000, rate: 0.75 },
        { minAum: 5000000, maxAum: 100000000, rate: 0.50 },
      ],
      accountCount: 32,
      totalAum: 125000000,
    },
    {
      id: 3,
      name: 'Flat Fee Basic',
      type: 'FLAT',
      rate: 2500,
      billingFrequency: 'QUARTERLY',
      accountCount: 45,
      totalAum: 18000000,
    },
    {
      id: 4,
      name: 'Performance Fee',
      type: 'PERFORMANCE',
      rate: 20.0,
      billingFrequency: 'ANNUALLY',
      accountCount: 12,
      totalAum: 35000000,
    },
  ];

  const mockPeriods: BillingPeriod[] = [
    {
      id: 1,
      periodStart: '2024-01-01',
      periodEnd: '2024-03-31',
      status: 'PAID',
      totalFees: 485000,
      accountsBilled: 174,
      createdAt: '2024-04-01',
    },
    {
      id: 2,
      periodStart: '2024-04-01',
      periodEnd: '2024-06-30',
      status: 'INVOICED',
      totalFees: 512000,
      accountsBilled: 180,
      createdAt: '2024-07-01',
    },
    {
      id: 3,
      periodStart: '2024-07-01',
      periodEnd: '2024-09-30',
      status: 'CALCULATED',
      totalFees: 528000,
      accountsBilled: 185,
      createdAt: '2024-10-01',
    },
    {
      id: 4,
      periodStart: '2024-10-01',
      periodEnd: '2024-12-31',
      status: 'PENDING',
      totalFees: 0,
      accountsBilled: 0,
      createdAt: '2025-01-01',
    },
  ];

  const mockFeeDetails: FeeDetail[] = [
    { householdName: 'Johnson Family', accountNumber: 'IRA-001234', aum: 450000, feeSchedule: 'Standard AUM', fee: 1125, billingDate: '2024-10-01', status: 'PENDING' },
    { householdName: 'Smith Trust', accountNumber: 'TRU-005678', aum: 2500000, feeSchedule: 'High Net Worth Tiered', fee: 4687, billingDate: '2024-10-01', status: 'PENDING' },
    { householdName: 'Chen Family Office', accountNumber: 'JNT-009012', aum: 8500000, feeSchedule: 'High Net Worth Tiered', fee: 12500, billingDate: '2024-10-01', status: 'PENDING' },
    { householdName: 'Williams Household', accountNumber: 'REV-003456', aum: 125000, feeSchedule: 'Flat Fee Basic', fee: 625, billingDate: '2024-10-01', status: 'PENDING' },
    { householdName: 'Miller Retirement', accountNumber: 'IRA-007890', aum: 350000, feeSchedule: 'Standard AUM', fee: 875, billingDate: '2024-10-01', status: 'PENDING' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try API calls, fallback to mock data
        const [schedulesRes, periodsRes] = await Promise.all([
          axios.get('/api/fee-billing/schedules').catch(() => ({ data: null })),
          axios.get('/api/fee-billing/periods').catch(() => ({ data: null })),
        ]);

        // Transform API data to match frontend interface
        const schedules = Array.isArray(schedulesRes.data) ? schedulesRes.data : [];
        const transformedSchedules = schedules.map((s: any, index: number) => ({
          id: s.id || index,
          name: s.scheduleName || s.name || 'Unnamed',
          type: (s.feeType || s.type || 'AUM').replace('AUM_PERCENTAGE', 'AUM').replace('AUM_TIERED', 'TIERED').replace('FLAT_FEE', 'FLAT'),
          rate: s.baseFeeBps ? Number(s.baseFeeBps) / 100 : Number(s.flatFeeAmount) || Number(s.rate) || 0,
          minFee: Number(s.minimumFee || s.minFee) || undefined,
          maxFee: Number(s.maximumFee || s.maxFee) || undefined,
          billingFrequency: s.billingFrequency || 'QUARTERLY',
          tiers: s.tiers,
          accountCount: Number(s.assignedAccountCount || s.accountCount) || 0,
          totalAum: Number(s.totalAum) || 0,
        }));
        setSchedules(transformedSchedules);

        const periods = Array.isArray(periodsRes.data) ? periodsRes.data : [];
        const transformedPeriods = periods.map((p: any, index: number) => ({
          id: p.id || index,
          billingPeriodId: p.billingPeriodId, // UUID for API calls
          periodStart: p.periodStart || p.startDate,
          periodEnd: p.periodEnd || p.endDate,
          status: p.status || 'PENDING',
          totalFees: Number(p.totalFees || p.totalFeesCalculated) || 0,
          accountsBilled: Number(p.accountsBilled || p.accountCount) || 0,
          createdAt: p.createdAt,
        }));
        setPeriods(transformedPeriods);

        setCurrentPeriodFees([]);
      } catch (error) {
        console.error('Error fetching fee billing data:', error);
        setSchedules([]);
        setPeriods([]);
        setCurrentPeriodFees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalAum = schedules.reduce((sum, s) => sum + (s.totalAum || 0), 0);
  const totalAccounts = schedules.reduce((sum, s) => sum + (s.accountCount || 0), 0);
  const ytdFees = periods.filter(p => p.status === 'PAID' || p.status === 'INVOICED').reduce((sum, p) => sum + (p.totalFees || 0), 0);
  const pendingFees = currentPeriodFees.reduce((sum, f) => sum + (f.fee || 0), 0);

  const handleExport = () => {
    // Export billing data as CSV
    const headers = ['Household', 'Account', 'AUM', 'Fee Schedule', 'Fee', 'Status'];
    const rows = currentPeriodFees.map(f => [
      f.householdName,
      f.accountNumber,
      f.aum,
      f.feeSchedule,
      f.fee,
      f.status
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fee-billing-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCalculateFees = async () => {
    try {
      const currentPeriod = periods.find(p => p.status === 'PENDING');
      if (currentPeriod && currentPeriod.billingPeriodId) {
        const response = await axios.post(`/api/fee-billing/periods/${currentPeriod.billingPeriodId}/calculate`);
        const result = response.data;
        alert(`Fees calculated successfully!\n\nPeriod: ${result.periodStart} - ${result.periodEnd}\nTotal Fees: $${(result.totalFees || 0).toLocaleString()}\nAccounts Billed: ${result.accountsBilled || 0}\nStatus: ${result.status}\n\nCheck the "Billing History" tab to see all periods.`);
        setActiveTab('periods');
        window.location.reload();
      } else if (currentPeriod) {
        alert('Billing period not properly configured. Please contact support.');
      } else {
        alert('No pending billing period found.\n\nTo create a billing period, use the API or seed data first.');
      }
    } catch (error: any) {
      console.error('Failed to calculate fees:', error);
      const message = error.response?.data?.message || 'Please try again.';
      alert(`Failed to calculate fees: ${message}`);
    }
  };

  const handleNewSchedule = () => {
    setShowNewScheduleModal(true);
  };

  const handleCreateSchedule = async () => {
    try {
      const payload = {
        scheduleName: newSchedule.scheduleName,
        feeType: newSchedule.feeType,
        baseFeeBps: newSchedule.feeType === 'AUM_PERCENTAGE' ? parseFloat(newSchedule.baseFeeBps) * 100 : null, // Convert % to bps
        flatFeeAmount: newSchedule.feeType === 'FLAT_FEE' ? parseFloat(newSchedule.flatFeeAmount) : null,
        billingFrequency: newSchedule.billingFrequency,
        minimumFee: newSchedule.minimumFee ? parseFloat(newSchedule.minimumFee) : null,
        description: newSchedule.description,
        effectiveDate: getTenantToday(),
      };

      await axios.post('/api/fee-billing/schedules', payload);
      alert('Fee schedule created successfully!');
      setShowNewScheduleModal(false);
      setNewSchedule({
        scheduleName: '',
        feeType: 'AUM_PERCENTAGE',
        baseFeeBps: '',
        flatFeeAmount: '',
        billingFrequency: 'QUARTERLY',
        minimumFee: '',
        description: '',
      });
      window.location.reload();
    } catch (error) {
      console.error('Failed to create fee schedule:', error);
      alert('Failed to create fee schedule. Please try again.');
    }
  };

  const handleGenerateInvoices = async () => {
    try {
      const calculatedPeriod = periods.find(p => p.status === 'CALCULATED');
      if (calculatedPeriod && calculatedPeriod.billingPeriodId) {
        const response = await axios.post(`/api/fee-billing/periods/${calculatedPeriod.billingPeriodId}/generate-invoices`);
        const result = response.data;
        alert(`Invoices generated successfully!\n\nPeriod: ${result.periodStart} - ${result.periodEnd}\nInvoices Generated: ${result.invoicesGenerated || 0}\nTotal Amount: $${(result.totalFees || 0).toLocaleString()}\nStatus: ${result.status}\n\nCheck the "Billing History" tab to see the updated status.`);
        setActiveTab('periods');
        window.location.reload();
      } else if (calculatedPeriod) {
        alert('Billing period not properly configured. Please contact support.');
      } else {
        alert('No calculated billing period found.\n\nYou must calculate fees first before generating invoices.\nClick "Calculate Fees" button first.');
      }
    } catch (error: any) {
      console.error('Failed to generate invoices:', error);
      const message = error.response?.data?.message || 'Please try again.';
      alert(`Failed to generate invoices: ${message}`);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Fee Billing</Title>
        <HeaderActions>
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button variant="success" onClick={handleCalculateFees}>Calculate Fees</Button>
          <Button variant="primary" onClick={handleNewSchedule}>+ New Schedule</Button>
        </HeaderActions>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatValue>{formatCurrency(totalAum)}</StatValue>
          <StatLabel>Total Billable AUM</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totalAccounts}</StatValue>
          <StatLabel>Billable Accounts</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(ytdFees)}</StatValue>
          <StatLabel>YTD Fees Collected</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue style={{ color: '#eab308' }}>{formatCurrency(pendingFees)}</StatValue>
          <StatLabel>Pending This Period</StatLabel>
        </StatCard>
      </StatsGrid>

      <TabContainer>
        <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          Current Period
        </Tab>
        <Tab active={activeTab === 'schedules'} onClick={() => setActiveTab('schedules')}>
          Fee Schedules
        </Tab>
        <Tab active={activeTab === 'periods'} onClick={() => setActiveTab('periods')}>
          Billing History
        </Tab>
      </TabContainer>

      {activeTab === 'overview' && (
        <Card>
          <CardHeader>
            <CardTitle>Q4 2024 Fee Calculation</CardTitle>
            <Button variant="primary" onClick={handleGenerateInvoices}>Generate Invoices</Button>
          </CardHeader>
          <Table>
            <thead>
              <tr>
                <Th>Household</Th>
                <Th>Account</Th>
                <Th>AUM</Th>
                <Th>Fee Schedule</Th>
                <Th>Fee</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {currentPeriodFees.map((fee, index) => (
                <tr key={index}>
                  <Td style={{ fontWeight: 600 }}>{fee.householdName}</Td>
                  <Td style={{ fontFamily: 'monospace' }}>{fee.accountNumber}</Td>
                  <Td>{formatCurrency(fee.aum)}</Td>
                  <Td>{fee.feeSchedule}</Td>
                  <Td style={{ fontWeight: 600 }}>{formatCurrency(fee.fee)}</Td>
                  <Td>
                    <StatusBadge status={fee.status}>{fee.status}</StatusBadge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {activeTab === 'schedules' && (
        <ScheduleGrid>
          {schedules.map((schedule) => (
            <ScheduleCard key={schedule.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <ScheduleName>{schedule.name}</ScheduleName>
                <TypeBadge type={schedule.type}>{schedule.type}</TypeBadge>
              </div>
              <ScheduleDetail>
                <span>Rate:</span>
                <span style={{ color: '#ffffff', fontWeight: 600 }}>
                  {schedule.type === 'FLAT' ? formatCurrency(schedule.rate) : `${schedule.rate}%`}
                </span>
              </ScheduleDetail>
              <ScheduleDetail>
                <span>Frequency:</span>
                <span>{schedule.billingFrequency}</span>
              </ScheduleDetail>
              <ScheduleDetail>
                <span>Accounts:</span>
                <span>{schedule.accountCount}</span>
              </ScheduleDetail>
              <ScheduleDetail>
                <span>Total AUM:</span>
                <span>{formatCurrency(schedule.totalAum)}</span>
              </ScheduleDetail>
              {schedule.minFee && (
                <ScheduleDetail>
                  <span>Minimum Fee:</span>
                  <span>{formatCurrency(schedule.minFee)}</span>
                </ScheduleDetail>
              )}
            </ScheduleCard>
          ))}
        </ScheduleGrid>
      )}

      {activeTab === 'periods' && (
        <Card>
          <CardTitle>Billing Periods</CardTitle>
          <Table style={{ marginTop: '1rem' }}>
            <thead>
              <tr>
                <Th>Period</Th>
                <Th>Status</Th>
                <Th>Accounts</Th>
                <Th>Total Fees</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {periods.map((period) => (
                <tr key={period.id}>
                  <Td>
                    {new Date(period.periodStart).toLocaleDateString()} - {new Date(period.periodEnd).toLocaleDateString()}
                  </Td>
                  <Td>
                    <StatusBadge status={period.status}>{period.status}</StatusBadge>
                  </Td>
                  <Td>{period.accountsBilled}</Td>
                  <Td style={{ fontWeight: 600 }}>{formatCurrency(period.totalFees)}</Td>
                  <Td>
                    <Button
                      variant="secondary"
                      style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                      onClick={() => alert(`Billing period details:\n\nPeriod: ${period.periodStart} - ${period.periodEnd}\nStatus: ${period.status}\nAccounts: ${period.accountsBilled}\nTotal Fees: ${formatCurrency(period.totalFees)}`)}
                    >
                      View Details
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* New Fee Schedule Modal */}
      {showNewScheduleModal && (
        <ModalOverlay onClick={() => setShowNewScheduleModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Create New Fee Schedule</ModalTitle>
              <CloseButton onClick={() => setShowNewScheduleModal(false)}>&times;</CloseButton>
            </ModalHeader>

            <FormGroup>
              <Label>Schedule Name *</Label>
              <Input
                type="text"
                placeholder="e.g., Standard AUM Fee"
                value={newSchedule.scheduleName}
                onChange={(e) => setNewSchedule({ ...newSchedule, scheduleName: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Fee Type *</Label>
              <Select
                value={newSchedule.feeType}
                onChange={(e) => setNewSchedule({ ...newSchedule, feeType: e.target.value })}
              >
                <option value="AUM_PERCENTAGE">AUM Percentage</option>
                <option value="AUM_TIERED">Tiered AUM</option>
                <option value="FLAT_FEE">Flat Fee</option>
                <option value="PERFORMANCE_FEE">Performance Fee</option>
              </Select>
            </FormGroup>

            {(newSchedule.feeType === 'AUM_PERCENTAGE' || newSchedule.feeType === 'AUM_TIERED') && (
              <FormGroup>
                <Label>Annual Fee Rate (%) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 1.0 for 1%"
                  value={newSchedule.baseFeeBps}
                  onChange={(e) => setNewSchedule({ ...newSchedule, baseFeeBps: e.target.value })}
                />
              </FormGroup>
            )}

            {newSchedule.feeType === 'FLAT_FEE' && (
              <FormGroup>
                <Label>Flat Fee Amount ($) *</Label>
                <Input
                  type="number"
                  step="100"
                  placeholder="e.g., 5000"
                  value={newSchedule.flatFeeAmount}
                  onChange={(e) => setNewSchedule({ ...newSchedule, flatFeeAmount: e.target.value })}
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label>Billing Frequency *</Label>
              <Select
                value={newSchedule.billingFrequency}
                onChange={(e) => setNewSchedule({ ...newSchedule, billingFrequency: e.target.value })}
              >
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="SEMI_ANNUALLY">Semi-Annually</option>
                <option value="ANNUALLY">Annually</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Minimum Fee ($)</Label>
              <Input
                type="number"
                step="100"
                placeholder="e.g., 500"
                value={newSchedule.minimumFee}
                onChange={(e) => setNewSchedule({ ...newSchedule, minimumFee: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <Input
                type="text"
                placeholder="Optional description"
                value={newSchedule.description}
                onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
              />
            </FormGroup>

            <ButtonGroup>
              <Button variant="secondary" onClick={() => setShowNewScheduleModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateSchedule}
                disabled={!newSchedule.scheduleName || (newSchedule.feeType === 'AUM_PERCENTAGE' && !newSchedule.baseFeeBps) || (newSchedule.feeType === 'FLAT_FEE' && !newSchedule.flatFeeAmount)}
              >
                Create Schedule
              </Button>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default FeeBillingScreen;
